// @ts-nocheck
// Tela 2 — Página interna de um Clube/Grupo.
import React, { useState } from 'react';
import { Icon } from '../icons';
import { MentionText, MentionChip, splitMentionsClamped } from '../mentions';
import { BookCover, Flame } from '../common';
import {
  useTerm, ClubBanner, Avatar, LivePulse, ReactionRow,
} from './components';
import { Club, RankingRow } from './data';
import { ClubWelcomeTour } from './welcome-tour';
import { CoverCropper } from './cover-cropper';
import { ClubBuyBooksModal } from './club-buy-modal';
import {
  SeriesPill, SeriesSection, MarkConcluidoCTA,
  ConfirmAdvanceModal, ManageReadingsSheet,
  useSeriesEngine, SeriesAnimStyles,
} from './series';
import { MuralDoClube } from './mural';

export const ClubInnerScreen: React.FC<{
  term?: 'clube' | 'grupo';
  // Quando definido, usa esse clube real (carregado do backend pelo App.tsx).
  club?: any;
  members?: any[];
  loading?: boolean;
  /** Status HTTP da falha ao carregar (404 sumiu, 403 é fechado). */
  loadError?: number | null;
  onBack?: () => void;
  onMenu?: () => void;
  onShare?: () => void;
  /** `escrever`: o Estudo abre já na escrita (pente fino, MOB-09). */
  onOpenEstudo?: (opts?: { escrever?: boolean }) => void;
  onAddBook?: () => void;
  /** Tap em "Continuar leitura" — leva pro BookDetailScreen (livro pessoal espelhado). */
  onContinueReading?: () => void;
  /** ID do usuário logado (vem da session) — usado pra destacar "você" na lista de membros. */
  currentUserId?: string;
  /** Quando admin confirma "Marcar como concluido" — chama API real pra promover o proximo. */
  onFinishCurrentBook?: (currentBookId: string) => Promise<void> | void;
  /** Tap num card do coverflow (livro nao-tbd): abre edicao desse livro. */
  onOpenSeriesBook?: (book: any) => void;
  /** Abre o Estudo de um livro especifico do historico (livros ja finalizados). */
  onOpenEstudoForBook?: (bookId: string) => void;
  /** Tap no card do livro atual → abre BookDetail do livro pessoal. */
  onOpenCurrentBook?: () => void;
  /** Livro pessoal espelhado do membro (pra info de "hoje X/meta Y" no card). */
  personalMirror?: any;
  /** Cutucar um membro do ranking — so dispara se admin habilitou. */
  onPokeMember?: (toUserId: string, name: string) => Promise<void> | void;
  /** Owner remove um membro (long-press) — Apple Guideline 1.2. */
  onRemoveMember?: (toUserId: string, name: string) => Promise<void> | void;
  /** Avulso (F4): persiste reorder da fila individual. */
  onReorderMemberBooks?: (ids: string[]) => Promise<void> | void;
  /** Owner/admin troca a capa do clube (upload). */
  onChangeBanner?: (file: any) => Promise<void> | void;
  onOpenProfile?: (userId: string) => void;
  /** Avulso (F5): user que esta sendo visto no momento. null/undefined = self. */
  viewingUserId?: string | null;
  /** Avulso (F5): troca visao pra ver fila de outro membro (chama getClub com viewingUserId). */
  onChangeViewingUser?: (userId: string | null) => Promise<void> | void;
  /** UX-6: entrar no clube quando o viewer ainda NÃO é membro (chegou pelo Explorar). */
  onJoin?: () => Promise<void> | void;
  /** Mural do clube (rodapé): fala direto com a API, precisa da sessão. */
  session?: any;
  myName?: string;
  myAvatarUrl?: string | null;
}> = ({ term = 'clube', club, members = [], loading = false, loadError = null, onBack, onMenu, onShare, onOpenEstudo, onAddBook, onContinueReading, onFinishCurrentBook, onOpenSeriesBook, onOpenEstudoForBook, onOpenCurrentBook, personalMirror, onPokeMember, onRemoveMember, currentUserId, onReorderMemberBooks, viewingUserId, onChangeViewingUser, onChangeBanner, onOpenProfile, onJoin, session, myName, myAvatarUrl }) => {
  const T = useTerm(term);
  // Tour de boas-vindas — dispara 1x por clube por device, quando o user ja eh membro.
  const [welcomeOpen, setWelcomeOpen] = React.useState(false);
  React.useEffect(() => {
    if (!club?.id || !club?.yourRole) return;
    const key = 'forjamente.welcomeSeen.' + club.id;
    let seen = false;
    try { seen = localStorage.getItem(key) === '1'; } catch {}
    if (!seen) { const t = setTimeout(() => setWelcomeOpen(true), 450); return () => clearTimeout(t); }
  }, [club?.id, club?.yourRole]);
  const closeWelcome = React.useCallback(() => {
    setWelcomeOpen(false);
    try { if (club?.id) localStorage.setItem('forjamente.welcomeSeen.' + club.id, '1'); } catch {}
  }, [club?.id]);
  const [tab, setTab] = useState('hoje');
  // Aba da página do clube (Hoje / Estudos[navega] / Cronograma / Membros).
  const [innerTab, setInnerTab] = useState<'hoje' | 'cronograma' | 'membros'>('hoje');
  const [joining, setJoining] = useState(false);
  // UX-6: viewer chegou pelo Explorar e ainda não é membro deste clube.
  const isVisitor = !!onJoin && !club?.yourRole;
  // CS-B: links de compra que o owner definiu (pro membro que ainda não tem o livro).
  // hasBuy é computado dentro do StudyEntryCard (onde `club` está); aqui só o estado
  // do modal + a lista que ele consome.
  const [buyOpen, setBuyOpen] = useState(false);
  const purchaseLinks = ((club as any)?.purchaseLinks || []).filter((b: any) => b && b.buyUrl);

  // Sequência de livros (multi-book). Estado local que avanca/reordena em cima
  // dos dados do backend — quando backend nao fornece club.series, hook fica
  // ocioso e nada novo aparece (compat retro).
  const { series, currentBook: seriesCurrent, nextBook: seriesNext, advancing, advance, reorder } = useSeriesEngine(club?.series || null);
  const hasSeries = !!series && series.totalPlanned > 1;
  // Avulso: ClubMySeries vem do backend em mySeries quando club.mode==='shared-habit'
  const mySeries = (club as any)?.mySeries || null;
  const hasMySeries = !!(mySeries && Array.isArray(mySeries.books) && mySeries.books.length > 0);
  const isAdmin = club?.yourRole === 'owner' || club?.yourRole === 'admin';
  const [manageOpen, setManageOpen] = useState(false);
  const [memberSwitcherOpen, setMemberSwitcherOpen] = useState(false);
  const [coverUploading, setCoverUploading] = React.useState(false);
  const coverInputRef = React.useRef<any>(null);
  const [coverPicked, setCoverPicked] = React.useState<any>(null);
  const [confirmAdvanceOpen, setConfirmAdvanceOpen] = useState(false);
  const [pokeBusyId, setPokeBusyId] = useState<string | null>(null);
  const [pokedIds] = useState(() => new Set<string>());

  // Beco sem saída, com saída. Antes qualquer falha caía no mesmo "Carregando…"
  // eterno: link de clube apagado, ou clube fechado de quem não é membro,
  // travavam numa tela sem mensagem e sem botão de voltar.
  if (!loading && !club && loadError) {
    const sumiu = loadError === 404;
    return (
      <div style={{ padding: '80px 28px', textAlign: 'center' }}>
        <div className="serif" style={{ fontSize: 24, color: 'var(--ink)', marginBottom: 10 }}>
          {sumiu ? 'Este clube não existe mais.' : 'Este clube é fechado.'}
        </div>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink-3)', margin: '0 0 22px' }}>
          {sumiu
            ? 'Ou o link está errado, ou quem criou apagou o clube.'
            : 'Só quem foi convidado entra. Peça um convite a quem te mandou o link.'}
        </p>
        {onBack && (
          <button onClick={onBack} style={{
            padding: '12px 22px', borderRadius: 999, cursor: 'pointer',
            background: 'var(--ink)', color: 'var(--paper)',
            border: 'none', fontSize: 14, fontFamily: 'inherit', fontWeight: 600,
          }}>
            Voltar
          </button>
        )}
      </div>
    );
  }

  if (loading || !club) {
    return (
      <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-3)' }}>
        Carregando…
      </div>
    );
  }

  const isGroupRead = club.mode === 'group-read';
  // Cutucar so disponivel quando admin habilitou nas configs do clube.
  // club.rules.allowPoke vem do GET /api/clubs/:id (mapClubForDesign).
  const allowPoke = club?.rules?.allowPoke !== false;
  const handlePoke = async (toUserId: string, name: string) => {
    if (!onPokeMember || pokeBusyId) return;
    setPokeBusyId(toUserId);
    try {
      await onPokeMember(toUserId, name);
      // So marca como cutucado DEPOIS que o servidor aceitou.
      pokedIds.add(toUserId);
    } catch (e) {
      // Quem chamou ja avisou a pessoa; aqui o que importa e nao marcar.
    } finally {
      setPokeBusyId(null);
    }
  };

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      <ClubWelcomeTour open={welcomeOpen} club={club} term={term} onClose={closeWelcome} />
      <ClubBuyBooksModal open={buyOpen} onClose={() => setBuyOpen(false)} links={purchaseLinks} term={term} />
      {/* UX-6: não-membro que abriu o clube pelo Explorar — barra fixa pra ENTRAR
          (antes ficava num "clube fantasma" vendo o feed sem como participar). */}
      {isVisitor && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(62px + env(safe-area-inset-bottom, 0px))', zIndex: 45, padding: '12px 16px', maxWidth: 460, margin: '0 auto' }}>
          <button
            onClick={async () => { if (joining || !onJoin) return; setJoining(true); try { await onJoin(); } finally { setJoining(false); } }}
            disabled={joining}
            style={{ width: '100%', padding: 15, borderRadius: 16, background: 'var(--ember)', color: 'var(--paper)', fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 12px 28px -8px rgba(199,83,26,0.55)' }}>
            {joining ? 'Entrando…' : <>Entrar neste {T.s}<Icon name="arrow-right" size={16} color="var(--paper)" stroke={2}/></>}
          </button>
        </div>
      )}
      {coverPicked && <CoverCropper file={coverPicked} aspect={4/3} outW={1440} outH={1080} title="Ajustar banner" hint="Arraste pra posicionar · proporção 4:3 (banner do clube)" onCancel={() => setCoverPicked(null)} onConfirm={async (cf) => { setCoverPicked(null); if (!onChangeBanner) return; setCoverUploading(true); try { await onChangeBanner(cf); } finally { setCoverUploading(false); } }} />}
      <div style={{ position: 'relative' }}>
        <ClubBanner hue={club.cover?.hue || 22} style={club.cover?.style || 'paper'} ratio={4/3} bannerUrl={club.customBannerUrl || null} coverUrl={club.customCoverUrl || club.cover?.customUrl || null} label={
          <>
            <button onClick={onBack} aria-label="Voltar" style={{
              position: 'absolute', top: 58, left: 16,
              width: 38, height: 38, borderRadius: '50%',
              background: 'rgba(20,16,14,0.55)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <Icon name="chevron-left" size={18} color="white"/>
            </button>
            {isAdmin && onChangeBanner && (
              <button onClick={() => coverInputRef.current && coverInputRef.current.click()} aria-label="Editar banner" style={{
                position: 'absolute', top: 58, right: 112,
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(20,16,14,0.55)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.15)', opacity: coverUploading ? 0.5 : 1,
              }}>
                <Icon name={coverUploading ? 'sync' : 'edit'} size={16} color="white" stroke={2}/>
                <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) setCoverPicked(f); }} />
              </button>
            )}
            {/* Compartilhar (gerar link de convite) — ícone à esquerda do "...". Same
                glass styling pra ficar coerente com back/menu. */}
            {onShare && (
              <button onClick={onShare} aria-label="Compartilhar convite" style={{
                position: 'absolute', top: 58, right: 64,
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(20,16,14,0.55)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.15)',
              }}>
                <Icon name="share" size={16} color="white" stroke={2}/>
              </button>
            )}
            <button onClick={onMenu} aria-label="Mais opções do clube" style={{
              position: 'absolute', top: 58, right: 16,
              width: 38, height: 38, borderRadius: '50%',
              background: 'rgba(20,16,14,0.55)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <Icon name="more" size={18} color="white"/>
            </button>
            <div style={{ position: 'absolute', left: 20, right: 20, bottom: 16 }}>
              {club.admin?.verified && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '3px 8px', borderRadius: 999,
                    background: 'rgba(255,255,255,0.92)',
                    color: 'var(--ember)',
                    fontFamily: 'Geist Mono, monospace',
                    fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
                    fontWeight: 600,
                  }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
                      <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {club.admin?.role || 'Líder'}
                  </span>
                </div>
              )}
              <div className="serif" style={{
                fontSize: 28, lineHeight: 1.05, color: 'white',
                letterSpacing: '-0.01em', textShadow: '0 2px 12px rgba(0,0,0,0.35)',
              }}>
                {club.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span onClick={() => onOpenProfile && club.admin?.userId && onOpenProfile(club.admin.userId)} style={{ cursor: (onOpenProfile && club.admin?.userId) ? 'pointer' : 'default', lineHeight: 0, flexShrink: 0 }}><Avatar letter={club.admin?.avatar || '?'} size={20} hue={club.cover?.hue || 22}/></span>
                <div className="mono" style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.92)', letterSpacing: '0.06em' }}>
                  {club.members || 0} {(club.members || 0) === 1 ? 'membro' : 'membros'}{club.activeToday > 0 ? <> · <span style={{ color: '#FFD580' }}>{club.activeToday} {club.activeToday === 1 ? 'ativo' : 'ativos'} hoje</span></> : null}
                </div>
              </div>
            </div>
          </>
        }/>
      </div>

      <div style={{
        padding: '14px 20px',
        background: 'var(--paper-2)',
        borderBottom: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* "lendo agora" vinha do servidor sempre 0 (o campo nunca foi
            calculado): um pulso ao vivo que nunca mexia. Mostra o que existe
            de verdade — quem leu hoje. */}
        <LivePulse count={club.activeToday || 0} label={(club.activeToday || 0) === 1 ? 'leu hoje' : 'leram hoje'}/>
        <button
          onClick={() => setInnerTab('membros')}
          style={{
            fontSize: 11, color: 'var(--ink-3)',
            padding: '4px 10px', borderRadius: 999,
            background: 'var(--paper)', border: '1px solid var(--line)',
            fontFamily: 'Geist Mono, monospace',
            letterSpacing: '0.06em', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          ver membros <Icon name="chevron-down" size={11} color="var(--ink-3)"/>
        </button>
      </div>

      {/* Abas do clube: Hoje / Estudos (navega) / Cronograma / Membros */}
      <div style={{ padding: '12px 20px 0', display: 'flex', gap: 4 }}>
        {([['hoje', 'Hoje'], ...(isGroupRead ? [['estudos', 'Estudos']] : []), ...((hasSeries || hasMySeries) ? [['cronograma', 'Cronograma']] : []), ['membros', 'Membros']] as any[]).map((t: any) => (
          <button key={t[0]} onClick={() => { if (t[0] === 'estudos') { if (!isVisitor) onOpenEstudo && onOpenEstudo(); } else setInnerTab(t[0]); }} style={{
            flex: 1, padding: '8px 4px', borderRadius: 9, fontSize: 11.5, fontWeight: 600,
            background: innerTab === t[0] ? 'var(--ink)' : 'var(--paper-2)',
            color: innerTab === t[0] ? 'var(--paper)' : 'var(--ink-3)',
            border: '1px solid var(--line)', cursor: 'pointer',
          }}>{t[1]}</button>
        ))}
      </div>

      {innerTab === 'hoje' && (<>
      <CurrentBookCard
        club={club}
        term={T}
        variant={isGroupRead ? 'group' : 'habit'}
        onAddBook={isVisitor ? onJoin : onAddBook}
        onContinue={isVisitor ? onJoin : onContinueReading}
        onOpenBook={isVisitor ? onJoin : onOpenCurrentBook}
        personalMirror={personalMirror}
        seriesPosition={hasSeries ? seriesCurrent?.position : undefined}
        seriesTotal={hasSeries ? series!.totalPlanned : undefined}
        isAdmin={isAdmin}
        onMarkConcluido={hasSeries ? () => setConfirmAdvanceOpen(true) : undefined}
        advancing={advancing}
      />

      {/* Estudo do livro vem PRIMEIRO (mais importante no dia-a-dia da
          leitura), Cronograma logo abaixo. */}
      {isGroupRead && (
        <div style={{ padding: '14px 20px 0' }}>
          <StudyEntryCard club={club} onClick={(o) => { if (isVisitor) { onJoin && onJoin(); } else if (onOpenEstudo) onOpenEstudo(o); }} onBuyClick={() => setBuyOpen(true)}/>
        </div>
      )}

      {/* Mural — último bloco da aba, DEPOIS do card de Estudo. É de propósito:
          quem abre o clube pra ler não esbarra nele, e quem precisa avisar o
          líder de algo (livro errado, título trocado) sabe onde procurar. */}
      {club?.id && (
        <MuralDoClube
          session={session}
          clubId={club.id}
          currentUserId={currentUserId}
          myName={myName}
          myAvatarUrl={myAvatarUrl}
          isVisitor={isVisitor}
          onOpenProfile={onOpenProfile}
        />
      )}
      </>)}

      {innerTab === 'cronograma' && (<>

      {/* Historico de estudos — lista compacta dos livros ja finalizados na
          sequencia. Vem entre o Estudo (acima) e o Cronograma (abaixo). Cada
          item abre o Estudo daquele livro especifico. Renderiza mesmo vazia
          (placeholder explicativo, compacto). */}
      {isGroupRead && hasSeries && (() => {
        const doneBooks = (series!.books || []).filter((b: any) => b.state === 'done' && b.id);
        return (
          <div style={{ padding: '12px 20px 0' }}>
            <div className="label" style={{ paddingBottom: 6 }}>Histórico de estudos</div>
            <div className="card" style={{ padding: doneBooks.length > 0 ? 4 : 10 }}>
              {doneBooks.length === 0 && (
                <div className="mono" style={{
                  textAlign: 'center', padding: '6px 4px',
                  fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase',
                  color: 'var(--ink-4)', lineHeight: 1.4,
                }}>
                  Cada livro concluído entra aqui
                </div>
              )}
              {doneBooks.map((b: any, i: number) => (
                <button
                  key={b.id}
                  onClick={() => onOpenEstudoForBook && onOpenEstudoForBook(b.id)}
                  style={{
                    width: '100%', padding: '6px 6px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'transparent',
                    borderTop: i === 0 ? 'none' : '1px solid var(--line)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    flexShrink: 0,
                    width: 22, height: 32, borderRadius: 3,
                    background: `linear-gradient(135deg, oklch(0.78 0.09 ${b.coverHue || 22}), oklch(0.45 0.10 ${b.coverHue || 22}))`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="serif" style={{ fontSize: 13, lineHeight: 1.15, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.title}
                    </div>
                    <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.08em', color: 'var(--ink-4)', marginTop: 1, textTransform: 'uppercase' }}>
                      #{b.position} · Lido
                    </div>
                  </div>
                  <Icon name="chevron-right" size={12} color="var(--ink-4)"/>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Cronograma (sequencia de livros) — so renderiza quando o clube tem
          series definida no backend. Compat: clubes antigos com 1 livro nao
          mostram nada novo. */}
      {isGroupRead && hasSeries && (
        <SeriesSection
          series={series!}
          isAdmin={isAdmin}
          isAdvancing={advancing}
          onManage={() => setManageOpen(true)}
          onPickTbd={() => onAddBook && onAddBook()}
          onAddBook={() => onAddBook && onAddBook()}
          onPickBook={(book) => onOpenSeriesBook && onOpenSeriesBook(book)}
        />
      )}

      {/* Avulso (shared-habit): pill de troca de visao. Aparece sempre que
          tem >1 membro com fila. Sutil — clicavel pra abrir sheet. */}
      {!isGroupRead && hasMySeries && (members?.length || 0) > 1 && (() => {
        const viewerName = (() => {
          if (!viewingUserId || mySeries.isMine !== false) return null;
          const m = members.find((mm: any) => mm.userId === viewingUserId);
          return m?.name || 'outro membro';
        })();
        return (
          <div style={{ padding: '14px 20px 0', display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => setMemberSwitcherOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 999,
                background: viewerName ? 'var(--ember-soft)' : 'var(--paper-2)',
                color: viewerName ? '#fff' : 'var(--ink-2)',
                border: '1px solid var(--line)', fontSize: 12,
              }}
            >
              <Icon name='users' size={12} color={viewerName ? '#fff' : 'var(--ink-3)'}/>
              {viewerName ? <>Vendo: <b>{viewerName}</b></> : <>Minha fila</>}
              <Icon name='chevron-down' size={10} color={viewerName ? '#fff' : 'var(--ink-3)'}/>
            </button>
          </div>
        );
      })()}

      {/* Avulso (shared-habit): SeriesSection lendo mySeries. F4 = edicao
          completa (add + reorder + finish), so na propria fila. Admin do clube
          NAO pode editar fila alheia — esse guard ja esta no backend tambem. */}
      {!isGroupRead && hasMySeries && (
        <SeriesSection
          series={{
            totalPlanned: mySeries.totalPlanned,
            currentIndex: mySeries.currentIndex,
            books: mySeries.books,
          } as any}
          isAdmin={mySeries.isMine !== false}
          isAdvancing={false}
          onManage={() => setManageOpen(true)}
          onPickTbd={() => onAddBook && onAddBook()}
          onAddBook={() => onAddBook && onAddBook()}
          onPickBook={(book) => onOpenSeriesBook && onOpenSeriesBook(book)}
        />
      )}
      </>)}

      <SeriesAnimStyles/>

      {manageOpen && hasSeries && (
        <ManageReadingsSheet
          series={series!}
          onClose={() => setManageOpen(false)}
          onReorder={reorder}
          onAdd={() => { setManageOpen(false); onAddBook && onAddBook(); }}
        />
      )}

      {/* ManageReadingsSheet pra Avulso — reorder persiste via onReorderMemberBooks. */}
      {manageOpen && !isGroupRead && hasMySeries && (
        <ManageReadingsSheet
          series={{
            totalPlanned: mySeries.totalPlanned,
            currentIndex: mySeries.currentIndex,
            books: mySeries.books,
          } as any}
          onClose={() => setManageOpen(false)}
          onReorder={(newBooks) => {
            // Persist via backend — ids na nova ordem
            const ids = newBooks.map((b: any) => b.memberBookId || b.id);
            if (onReorderMemberBooks) {
              Promise.resolve(onReorderMemberBooks(ids)).catch(() => {});
            }
          }}
          onAdd={() => { setManageOpen(false); onAddBook && onAddBook(); }}
        />
      )}
      {confirmAdvanceOpen && (
        <ConfirmAdvanceModal
          currentBook={seriesCurrent || undefined}
          nextBook={seriesNext}
          onConfirm={() => {
            setConfirmAdvanceOpen(false);
            // Otimista: avanca local pra UX fluida; backend e re-fetch via callback.
            advance();
            if (seriesCurrent?.id && onFinishCurrentBook) {
              Promise.resolve(onFinishCurrentBook(seriesCurrent.id)).catch(() => {});
            }
          }}
          onClose={() => setConfirmAdvanceOpen(false)}
        />
      )}

      {/* UX-3: função de eventos/encontros NÃO foi implementada de verdade — oculta
          até existir (antes mostrava data fixa "MAI 22"). Mantido o bloco p/ futuro. */}
      {false && club.nextEvent && (
        <div style={{ padding: '14px 20px 0' }}>
          <div className="label" style={{ paddingBottom: 8 }}>Próximo encontro</div>
          <div style={{
            padding: 16, borderRadius: 18,
            background: 'var(--ink)', color: 'var(--paper)',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              flexShrink: 0,
              width: 52, height: 52, borderRadius: 14,
              background: 'var(--ember)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              color: 'var(--paper)',
            }}>
              <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.16em' }}>MAI</div>
              <div className="serif" style={{ fontSize: 22, lineHeight: 1 }}>22</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="serif" style={{ fontSize: 16, lineHeight: 1.2 }}>{club.nextEvent.title}</div>
              <div className="mono" style={{
                fontSize: 10.5, color: 'var(--ink-4)',
                marginTop: 4, letterSpacing: '0.06em',
              }}>
                {club.nextEvent.date} · {club.nextEvent.link}
              </div>
            </div>
            <Icon name="chevron-right" size={16} color="var(--paper)"/>
          </div>
        </div>
      )}

      {innerTab === 'membros' && (<>
      <div className="section-head" id="club-ranking">
        <div>
          <div className="label">Ritmo entre todos</div>
          <div className="serif title" style={{ marginTop: 4 }}>Ranking · membros</div>
          {/* Só com alguém além da líder para remover (QA-10). */}
          {club?.yourRole === 'owner' && members.length > 1 && (
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.10em', color: 'var(--ink-4)',
              marginTop: 6, textTransform: 'uppercase',
            }}>
              Toque e segure num membro para removê-lo
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div style={{
          padding: 4, background: 'var(--paper-2)', borderRadius: 12,
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4,
          marginBottom: 12,
        }}>
          {['Hoje', 'Semana', 'Mês', 'Total'].map((t) => (
            <button key={t} onClick={() => setTab(t.toLowerCase())} style={{
              padding: '8px 4px', borderRadius: 9,
              background: tab === t.toLowerCase() ? 'var(--paper)' : 'transparent',
              fontSize: 12, fontWeight: 500,
              color: 'var(--ink)',
              boxShadow: tab === t.toLowerCase() ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
            }}>
              {t}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.length === 0 && (
            <div style={{
              padding: 28, textAlign: 'center',
              border: '1px dashed var(--line-strong)',
              borderRadius: 18, color: 'var(--ink-3)', fontSize: 13,
            }}>
              <div className="serif" style={{ fontSize: 18, fontStyle: 'italic', marginBottom: 4 }}>
                Aguardando os primeiros leitores
              </div>
              <div>Convide alguém pelo menu (...) ou pelo link de convite.</div>
            </div>
          )}
          {(() => {
            // Ordena membros pelo tab selecionado (hoje/semana/mes/total).
            const key = tab === 'semana' ? 'pagesWeek'
              : tab === 'mês' ? 'pagesMonth'
              : tab === 'total' ? 'pagesTotal'
              : 'pagesToday';
            const sorted = [...members].sort((a: any, b: any) => (b[key] || 0) - (a[key] || 0));
            return sorted.map((m: any, i: number) => (
              <RankRow key={m.userId} row={{
                userId: m.userId,
                rank: i + 1,
                name: m.name,
                avatar: m.avatar,
                pages: m[key] || 0,
                streak: 0,
                isYou: !!currentUserId && m.userId === currentUserId,
                inactive: (m[key] || 0) === 0 && tab !== 'total',
                // O que "sem leitura" quer dizer nesta aba (pente fino, QA-10):
                // o rótulo era "sumido há {lastSeenDays}d", e esse número
                // nunca vem do servidor — saía "sumido há d".
                semLeitura: tab === 'semana' ? 'sem ler na semana' : tab === 'mês' ? 'sem ler no mês' : 'sem ler hoje',
              } as any}
              allowPoke={allowPoke && !!currentUserId && m.userId !== currentUserId}
              onPoke={handlePoke}
              pokeBusyId={pokeBusyId}
              pokedIds={pokedIds}
              canRemove={club?.yourRole === 'owner' && m.userId !== currentUserId}
              onRemove={onRemoveMember}
              onOpenProfile={onOpenProfile}
              />
            ));
          })()}
        </div>
      </div>
      </>)}

      {/* F5: MemberSwitcher — sheet com lista de membros pra trocar de visao. */}
      {memberSwitcherOpen && !isGroupRead && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9000, display: 'flex', alignItems: 'flex-end',
        }} onClick={() => setMemberSwitcherOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', background: 'var(--paper)', borderRadius: '20px 20px 0 0',
            padding: 24, maxHeight: '70vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className='serif' style={{ fontSize: 22 }}>Ver fila de</div>
              <button onClick={() => setMemberSwitcherOpen(false)} style={{
                width: 32, height: 32, borderRadius: '50%', background: 'var(--paper-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Icon name='close' size={16}/></button>
            </div>
            <button
              onClick={() => { setMemberSwitcherOpen(false); onChangeViewingUser && onChangeViewingUser(null); }}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 14,
                background: (!viewingUserId || mySeries?.isMine !== false) ? 'var(--ember)' : 'var(--paper-2)',
                color: (!viewingUserId || mySeries?.isMine !== false) ? '#fff' : 'var(--ink-2)',
                fontSize: 14, fontWeight: 500, marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
              <Icon name='user' size={16} color={(!viewingUserId || mySeries?.isMine !== false) ? '#fff' : 'var(--ink-3)'}/>
              Minha fila
            </button>
            {members.filter((m: any) => m.userId && m.userId !== currentUserId).map((m: any) => {
              const active = viewingUserId === m.userId;
              return (
                <button
                  key={m.userId}
                  onClick={() => { setMemberSwitcherOpen(false); onChangeViewingUser && onChangeViewingUser(m.userId); }}
                  style={{
                    width: '100%', padding: '14px 16px', borderRadius: 14,
                    background: active ? 'var(--ember-soft)' : 'var(--paper-2)',
                    color: active ? '#fff' : 'var(--ink-2)',
                    fontSize: 14, marginBottom: 8, textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                  <Icon name='user' size={16} color={active ? '#fff' : 'var(--ink-3)'}/>
                  {m.name || 'Membro'}
                </button>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

// Card "Estudo do livro" — entrada pra área de Estudo. Mostra o marco ativo
// (se houver), última atividade real (kind + autor + tempo relativo) e avatars
// dos últimos 3 contribuidores únicos. Quando ainda não houve nenhum post, vira
// CTA "Comece a estudar" para empurrar o user a postar primeiro.
const StudyEntryCard: React.FC<{ club: any; onClick: (opts?: { escrever?: boolean }) => void; onBuyClick?: () => void }> = ({ club, onClick, onBuyClick }) => {
  // CS-B: tem link de compra (definido pelo owner)? hasBuy computado AQUI, onde
  // `club` existe (a state buyOpen vive no ClubInnerScreen e chega via onBuyClick).
  const hasBuy = ((club?.purchaseLinks || []).filter((b: any) => b && b.buyUrl).length) > 0 && !!onBuyClick;
  const summary = club.studySummary || null;
  const milestoneN = summary?.milestoneN ?? null;
  const milestoneActive = !!summary?.milestoneActive;
  const newToday = summary?.newToday || 0;
  const latest = summary?.latestActivity || null;
  const recent = summary?.recentAuthors || [];
  const empty = !latest;

  // Mapeia o kind do post pro verbo amigável da timeline.
  // (Mesmas categorias que o feed: reflection|citation|question|quiz|question-box|leader-answer|insight-final)
  function describeKind(k: string): string {
    switch (k) {
      case 'reflection':       return 'compartilhou uma reflexão';
      case 'citation':         return 'destacou uma citação';
      case 'question':         return 'fez uma pergunta';
      case 'quiz':             return 'criou um quiz';
      case 'question-box':     return 'abriu uma caixa de perguntas';
      case 'leader-answer':    return 'respondeu uma pergunta';
      case 'insight-final':    return 'publicou o insight final';
      default:                 return 'postou';
    }
  }

  function relativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  }

  return (
    // Sem nada publicado, o cartão promete "Comece o estudo — poste a 1ª
    // reflexão": então abre a escrita, não só a tela (pente fino, MOB-09).
    <button onClick={() => onClick(empty ? { escrever: true } : undefined)} style={{
      width: '100%', padding: 0, borderRadius: 22,
      background: 'linear-gradient(155deg, #1A1614 0%, #2A2320 100%)',
      color: '#F3ECDE',
      position: 'relative', overflow: 'hidden',
      textAlign: 'left', border: 'none', display: 'block',
    }}>
      <div style={{
        position: 'absolute', top: -50, right: -50,
        width: 180, height: 180, borderRadius: '50%',
        background: 'radial-gradient(circle, var(--ember) 0%, transparent 65%)',
        opacity: 0.45, filter: 'blur(28px)',
        pointerEvents: 'none',
      }}/>

      <div style={{ padding: '18px 20px 16px', position: 'relative' }}>
        {/* Cabeçalho — pílula do marco (real) + contagem de novas hoje (real) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          {milestoneActive && milestoneN ? (
            <span className="mono" style={{
              padding: '3px 8px', borderRadius: 999,
              background: 'var(--ember)', color: 'var(--paper)',
              fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              ✦ Marco {milestoneN} ativo
            </span>
          ) : (
            <span className="mono" style={{
              padding: '3px 8px', borderRadius: 999,
              background: 'rgba(243,236,222,0.08)', color: 'rgba(243,236,222,0.7)',
              fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 600,
            }}>
              {milestoneN ? `Marco ${milestoneN}` : 'Sem marco ativo'}
            </span>
          )}
          {newToday > 0 ? (
            <span className="mono" style={{
              fontSize: 10, letterSpacing: '0.06em',
              color: 'rgba(243,236,222,0.6)',
            }}>
              {newToday} {newToday === 1 ? 'nova hoje' : 'novas hoje'}
            </span>
          ) : null}
        </div>

        <div className="serif" style={{
          fontSize: 26, lineHeight: 1.05, letterSpacing: '-0.005em',
          color: '#F3ECDE',
        }}>
          Estudo <span style={{ fontStyle: 'italic', color: 'var(--ember-2)' }}>do livro</span>
        </div>
        {/* Subtitle ja diz qual livro pertence a este Estudo — pra que o user
            distinga rapidamente do historico de estudos abaixo. */}
        {club?.book?.title && (
          <div className="mono" style={{
            marginTop: 6, fontSize: 10, letterSpacing: '0.14em',
            color: 'var(--ember-2)', textTransform: 'uppercase', fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {club.book.title}
          </div>
        )}
        {/* CS-B: link de compra (se o owner definiu) — pro membro que ainda não tem o livro. */}
        {hasBuy && (
          <button onClick={onBuyClick} style={{
            marginTop: 10, padding: '8px 13px', borderRadius: 999,
            background: 'rgba(232,122,59,0.16)', border: '1px solid rgba(232,122,59,0.4)',
            color: 'var(--ember-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            <Icon name="bookmark" size={13} color="var(--ember-2)" />
            Ainda não tem o livro? Adquira aqui
            <Icon name="arrow-right" size={13} color="var(--ember-2)" stroke={2} />
          </button>
        )}
        {/* CS-C: nomeia o caminho "só acompanhar" pra quem ainda não começou o livro
            (só quando já há livro atual definido). */}
        {club?.book?.title && !club?.book?.personalBookId && (
          <div style={{ fontSize: 11.5, color: 'rgba(243,236,222,0.6)', marginTop: hasBuy ? 9 : 10, lineHeight: 1.4 }}>
            Não precisa ter o livro pra participar — dá pra <strong style={{ color: 'rgba(243,236,222,0.85)' }}>só acompanhar os estudos</strong> por aqui.
          </div>
        )}
        <div style={{
          fontSize: 12.5, color: 'rgba(243,236,222,0.7)', marginTop: 6,
          lineHeight: 1.4,
        }}>
          Reflexões, citações, perguntas e quizzes — organizados pelos marcos da leitura.
        </div>

        {/* Linha de atividade real — autor + verbo + tempo. Sem dado, vira CTA. */}
        <div style={{
          marginTop: 14, padding: '10px 12px',
          background: 'rgba(243,236,222,0.05)',
          borderRadius: 12,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {empty ? (
            <>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                background: 'var(--ember-soft)', color: 'var(--ember)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 600,
                border: '2px solid #2A2320',
              }}>+</div>
              <div style={{ flex: 1, fontSize: 11.5, color: 'rgba(243,236,222,0.85)' }}>
                <strong style={{ color: '#F3ECDE', fontWeight: 600 }}>Comece o estudo</strong> — poste a 1ª reflexão.
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex' }}>
                {(recent.length > 0 ? recent : [{ initial: latest!.authorInitial, name: latest!.authorName }]).map((a: any, i: number) => (
                  <div key={i} style={{
                    marginLeft: i === 0 ? 0 : -8,
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--ember-soft)', color: 'var(--ember)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 600,
                    border: '2px solid #2A2320',
                  }}>{a.initial}</div>
                ))}
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: 'rgba(243,236,222,0.85)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong style={{ color: '#F3ECDE', fontWeight: 600 }}>{latest!.authorName}</strong>{' '}
                {describeKind(latest!.kind)}
                <span style={{ color: 'rgba(243,236,222,0.5)' }}> · {relativeTime(latest!.createdAt)}</span>
              </div>
              <Icon name="chevron-right" size={14} color="rgba(243,236,222,0.7)"/>
            </>
          )}
        </div>
      </div>
    </button>
  );
};

const CurrentBookCard: React.FC<{
  club: Club; term: any;
  variant?: 'group' | 'habit';
  onAddBook?: () => void;
  onContinue?: () => void;
  /** Toca no card (capa/titulo) → abre BookDetail do livro pessoal. */
  onOpenBook?: () => void;
  /** Livro pessoal espelhado — fonte pra meta diaria e paginas lidas hoje. */
  personalMirror?: any;
  // Multi-book sequence props
  seriesPosition?: number;
  seriesTotal?: number;
  isAdmin?: boolean;
  onMarkConcluido?: () => void;
  advancing?: boolean;
}> = ({ club, term, variant = 'group', onAddBook, onContinue, onOpenBook, personalMirror, seriesPosition, seriesTotal, isAdmin: isAdminProp, onMarkConcluido, advancing = false }) => {
  const isAdmin = typeof isAdminProp === 'boolean' ? isAdminProp : (club.yourRole === 'owner' || club.yourRole === 'admin');
  const isHabit = variant === 'habit';
  const hasPill = !!(seriesTotal && seriesTotal > 1 && seriesPosition);
  // Em group-read usa o livro compartilhado do clube; em shared-habit usa o
  // livro pessoal do membro (cada um tem o seu, mas visual idêntico).
  const sourceBook: any = isHabit ? (club as any).myBook : (club as any).book;
  // CTA do empty state: admin no group-read (só líder define); qualquer membro
  // no shared-habit (cada um adiciona o seu).
  const canAdd = isHabit ? true : isAdmin;
  if (!sourceBook) {
    return (
      <div style={{ padding: '20px 20px 0' }}>
        <div className="label" style={{ paddingBottom: 8 }}>
          {isHabit ? 'Meu livro' : `Livro do ${term.s}`}
        </div>
        <div className="card" style={{
          padding: 20, textAlign: 'center',
          border: '1px dashed var(--line-strong)',
          background: 'var(--paper-2)',
        }}>
          <div style={{
            width: 64, height: 64, margin: '0 auto', borderRadius: 12,
            background: 'var(--paper-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 28 }}>📚</span>
          </div>
          <div className="serif" style={{ fontSize: 20, lineHeight: 1.2, marginTop: 14 }}>
            {isHabit ? 'Adicione seu livro' : `Defina o livro do ${term.s}`}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
            {isHabit
              ? <>Esse {term.s} é de leitura avulsa — cada membro escolhe o seu. <br/>Toque abaixo para adicionar o seu.</>
              : isAdmin
                ? <>Esse {term.s} é de leitura em grupo, mas ainda não tem livro escolhido. <br/>Toque abaixo para escolher.</>
                : <>Esse {term.s} é de leitura em grupo, mas o líder ainda não escolheu o livro.</>}
          </div>
          {canAdd && onAddBook && (
            <button onClick={onAddBook} style={{
              marginTop: 18, padding: '12px 22px', borderRadius: 14,
              background: 'var(--ember)', color: 'var(--paper)',
              fontSize: 14, fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              boxShadow: '0 12px 24px -10px rgba(199,83,26,0.55)',
            }}>
              <Icon name="plus" size={14} color="var(--paper)" stroke={2.4}/>
              {isHabit ? 'Adicionar meu livro' : `Adicionar livro do ${term.s}`}
            </button>
          )}
        </div>
      </div>
    );
  }
  const b = sourceBook;
  const behind = (b.pagesBehind || 0) > 0;
  const ahead = (b.pagesBehind || 0) < 0;
  return (
    <div style={{ padding: '20px 20px 0' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingBottom: 8, gap: 10,
      }}>
        <div className="label">Livro atual</div>
        {hasPill && <SeriesPill position={seriesPosition} total={seriesTotal}/>}
      </div>
      <div className="card" style={{
        padding: 18,
        transition: 'all 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        opacity: advancing ? 0 : 1,
        transform: advancing ? 'translateY(-12px) scale(0.98)' : 'none',
      }}>
        {(() => {
          // Info adicional vem do backend (snapshot fresh do reading_history),
          // nao do personalMirror local que pode estar stale. Fallback pro
          // espelho pessoal quando o backend nao expoe.
          const today = (() => { const d = new Date(); return d.toISOString().slice(0, 10); })();
          const todayPages = typeof b.yourPagesToday === 'number'
            ? b.yourPagesToday
            : (personalMirror
                ? (personalMirror.sessions || []).filter((s: any) => s.date === today).reduce((t: number, s: any) => t + (s.pages || 0), 0)
                : 0);
          const dailyGoal = b.dailyGoal || personalMirror?.dailyGoal || 0;
          const collectiveAvgPage = b.collectiveAvgPage ?? Math.round(((b.collectivePct || 0) / 100) * (b.totalPages || 0));
          const Inner = (
            <div style={{ display: 'flex', gap: 16 }}>
              <BookCover book={{ title: b.title, author: b.author, coverHue: b.coverHue, coverStyle: b.coverStyle, coverUrl: b.coverUrl } as any} width={86} height={128}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="serif" style={{ fontSize: 22, lineHeight: 1.1 }}>{b.title}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{b.author}</div>
                {(b.startDate || b.endDate) && (
                  <div className="mono" style={{
                    fontSize: 10, color: 'var(--ink-4)',
                    letterSpacing: '0.08em', marginTop: 8,
                  }}>
                    {b.startDate || '—'} → {b.endDate || '—'}
                  </div>
                )}
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                    <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
                      {term.S}: <strong style={{ color: 'var(--ember)', fontWeight: 600 }}>{collectiveAvgPage}</strong> / {b.totalPages}
                    </span>
                    <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--ink-4)' }}>
                      você: <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{b.yourPage}</strong> / {b.totalPages}
                    </span>
                  </div>
                  <div style={{ position: 'relative', height: 5, background: 'var(--paper-3)', borderRadius: 3, overflow: 'visible' }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${b.collectivePct}%`, background: 'var(--ember)', borderRadius: 3,
                      boxShadow: '0 0 8px rgba(199,83,26,0.4)',
                    }}/>
                    <div style={{
                      position: 'absolute', top: -3, bottom: -3,
                      left: `${b.yourPct}%`, width: 2,
                      background: 'var(--ink)', borderRadius: 1,
                    }}/>
                  </div>
                  {/* Mini chip "Hoje X/meta Y" — so quando ha espelho pessoal */}
                  {personalMirror && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="mono" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 10px', borderRadius: 999,
                        background: (dailyGoal > 0 && todayPages >= dailyGoal) ? 'var(--ember)' : 'var(--paper-3)',
                        color: (dailyGoal > 0 && todayPages >= dailyGoal) ? 'var(--paper)' : 'var(--ink-2)',
                        fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600,
                      }}>
                        Hoje
                        <span style={{ fontWeight: 700 }}>
                          {dailyGoal > 0 ? `${todayPages}/${dailyGoal}` : todayPages}
                        </span>
                        pág
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
          return onOpenBook ? (
            <button onClick={onOpenBook} style={{
              width: '100%', padding: 0, margin: 0, background: 'transparent',
              border: 'none', cursor: 'pointer', textAlign: 'left',
            }}>
              {Inner}
            </button>
          ) : Inner;
        })()}
        <div style={{
          marginTop: 16, padding: '12px 14px',
          borderRadius: 12,
          background: behind ? 'rgba(199,83,26,0.08)' : ahead ? 'rgba(94,113,86,0.10)' : 'var(--paper-3)',
          border: `1px solid ${behind ? 'rgba(199,83,26,0.18)' : ahead ? 'rgba(94,113,86,0.22)' : 'var(--line)'}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            flexShrink: 0,
            width: 32, height: 32, borderRadius: 10,
            background: behind ? 'var(--ember-soft)' : ahead ? 'var(--sage-soft)' : 'var(--paper)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name={behind ? 'arrow-down' : ahead ? 'arrow-up' : 'check'} size={16}
              color={behind ? 'var(--ember)' : ahead ? 'var(--sage)' : 'var(--ink-3)'}/>
          </div>
          <div style={{ flex: 1 }}>
            <div className="serif" style={{ fontSize: 15, lineHeight: 1.2, fontStyle: 'italic' }}>
              {behind && `Você está ${b.pagesBehind} páginas atrás do ritmo.`}
              {ahead && `Você está ${Math.abs(b.pagesBehind || 0)} páginas à frente.`}
              {!behind && !ahead && (isHabit ? 'No seu ritmo.' : `No ritmo do ${term.s}.`)}
            </div>
          </div>
        </div>
        <button onClick={onContinue} style={{
          width: '100%', marginTop: 14,
          padding: '14px 18px', borderRadius: 16,
          background: 'var(--ink)', color: 'var(--paper)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontSize: 15, fontWeight: 500,
        }}>
          Continuar leitura <Icon name="arrow-right" size={16} color="var(--paper)"/>
        </button>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <span className="mono" style={{ fontSize: 9.5, letterSpacing: '0.1em', color: 'var(--ink-4)' }}>
            toque em Continuar leitura para registrar suas páginas
          </span>
        </div>

        {/* Admin · Marcar como concluído (so quando o clube tem sequencia de
            livros — single-book preserva o card antigo sem CTA novo). */}
        {isAdmin && onMarkConcluido && <MarkConcluidoCTA onClick={onMarkConcluido}/>}
      </div>
    </div>
  );
};

const RankRow: React.FC<{
  row: RankingRow & { userId?: string };
  highlightYou?: boolean;
  allowPoke?: boolean;
  onPoke?: (toUserId: string, name: string) => Promise<void> | void;
  pokeBusyId?: string | null;
  pokedIds?: Set<string>;
  /** Owner pode remover membros via long-press (Apple Guideline 1.2). */
  canRemove?: boolean;
  onRemove?: (toUserId: string, name: string) => Promise<void> | void;
  onOpenProfile?: (userId: string) => void;
}> = ({ row, highlightYou = false, allowPoke = false, onPoke, pokeBusyId, pokedIds, canRemove = false, onRemove, onOpenProfile }) => {
  // Hooks ANTES de qualquer early return (regra do React).
  const lpRef = React.useRef<{ timer: any; fired: boolean; sx?: number; sy?: number }>({ timer: null, fired: false });
  if (!row) return null;
  const isTop3 = row.rank <= 3;
  const isInactive = !!row.inactive;
  const medalColor = ['#D4A24C', '#B8B8C0', '#C57A4A'][row.rank - 1];
  const uid = (row as any).userId as string | undefined;
  const wasPoked = !!(uid && pokedIds?.has(uid));
  const isPoking = pokeBusyId === uid;
  const openProf = () => { if (lpRef.current.fired) return; if (uid && onOpenProfile) onOpenProfile(uid); };
  const startLongPress = (e: any) => {
    if (!canRemove || !uid || !onRemove || highlightYou) return;
    if (lpRef.current.timer) clearTimeout(lpRef.current.timer);
    lpRef.current.fired = false;
    lpRef.current.sx = e && e.clientX; lpRef.current.sy = e && e.clientY;
    lpRef.current.timer = setTimeout(() => {
      lpRef.current.fired = true;
      if (window.confirm(`Remover ${row.name} do grupo?\n\nO membro perde acesso ao conteúdo e livros vinculados são desvinculados. Esta ação não pode ser desfeita.`)) {
        try { onRemove(uid, row.name); } catch {}
      }
    }, 600);
  };
  const cancelLongPress = () => {
    if (lpRef.current.timer) { clearTimeout(lpRef.current.timer); lpRef.current.timer = null; }
  };
  const moveLongPress = (e: any) => {
    if (!lpRef.current.timer) return;
    const dx = (e.clientX || 0) - (lpRef.current.sx || 0), dy = (e.clientY || 0) - (lpRef.current.sy || 0);
    if (dx * dx + dy * dy > 100) cancelLongPress();
  };

  return (
    <div
      onPointerDown={startLongPress}
      onPointerMove={moveLongPress}
      onPointerUp={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onPointerLeave={cancelLongPress}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px',
        background: highlightYou ? '#1A1614' : 'var(--paper-2)',
        color: highlightYou ? '#F3ECDE' : 'var(--ink)',
        borderRadius: 14,
        border: highlightYou ? '1px solid var(--ember)'
          : isInactive ? '1px dashed var(--line-strong)' : '1px solid var(--line)',
        boxShadow: highlightYou ? '0 12px 28px -16px rgba(199,83,26,0.5)' : 'none',
        opacity: isInactive ? 0.55 : 1,
        transition: 'opacity 200ms',
        userSelect: canRemove ? 'none' : undefined,
      }}>
      <div style={{
        flexShrink: 0,
        width: 32, height: 32, borderRadius: '50%',
        background: isTop3 ? medalColor : (highlightYou ? '#3B322C' : 'var(--paper)'),
        color: isTop3 ? 'white' : (highlightYou ? '#F3ECDE' : 'var(--ink-3)'),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Instrument Serif, serif',
        fontSize: 15, fontStyle: 'italic',
        boxShadow: isTop3 ? `inset 0 0 0 1px rgba(255,255,255,0.3), 0 2px 6px ${medalColor}55` : 'none',
      }}>
        {row.rank}
      </div>
      <div onClick={openProf} style={{ filter: isInactive ? 'grayscale(0.85)' : 'none', cursor: onOpenProfile && uid ? 'pointer' : undefined }}>
        <Avatar letter={row.avatar} size={32} hue={row.rank * 40} ring={highlightYou}/>
      </div>
      <div onClick={openProf} style={{ flex: 1, minWidth: 0, cursor: onOpenProfile && uid ? 'pointer' : undefined }}>
        <div style={{
          fontSize: 14, fontWeight: highlightYou ? 600 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: highlightYou ? '#F3ECDE' : 'var(--ink)',
        }}>
          {row.name}
          {highlightYou && (
            <span style={{
              fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
              color: 'var(--ember-2)', fontSize: 14, fontWeight: 400,
            }}> · você</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <span className="mono" style={{
            fontSize: 10, letterSpacing: '0.06em',
            color: highlightYou ? 'rgba(243,236,222,0.65)' : 'var(--ink-3)',
          }}>
            {row.pages} pág
          </span>
          <span style={{ width: 2, height: 2, background: 'currentColor', opacity: 0.4, borderRadius: 1 }}/>
          {isInactive ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, opacity: 0.5, filter: 'grayscale(1)' }}>🌙</span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.04em' }}>
                {row.lastSeenDays != null ? `sumido há ${row.lastSeenDays}d` : (row.semLeitura || 'sem leitura')}
              </span>
            </span>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Flame count={row.streak} size={12}/>
              <span className="mono" style={{ fontSize: 10, color: highlightYou ? 'var(--ember-2)' : 'var(--ember)' }}>
                {row.streak}d
              </span>
            </span>
          )}
        </div>
      </div>
      {/* Botao cutucar — so renderiza se admin habilitou (allowPoke) E nao
          e o proprio user. Wesley: o admin habilita/desabilita nas configs do
          grupo; quando desligado, ninguem ve o botao. */}
      {!highlightYou && allowPoke && uid && (
        <button
          onClick={() => !wasPoked && !isPoking && onPoke && onPoke(uid, row.name)}
          disabled={wasPoked || isPoking}
          style={{
            flexShrink: 0,
            padding: '6px 10px', borderRadius: 999,
            background: wasPoked ? 'transparent' : isInactive ? 'var(--ember-soft)' : 'var(--paper)',
            border: '1px solid ' + (wasPoked ? 'var(--sage)' : isInactive ? 'color-mix(in oklab, var(--ember) 30%, transparent)' : 'var(--line)'),
            fontSize: 11, color: wasPoked ? 'var(--sage)' : isInactive ? 'var(--ember)' : 'var(--ink-2)',
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontWeight: isInactive ? 500 : 400,
            opacity: isPoking ? 0.5 : 1,
            cursor: wasPoked || isPoking ? 'default' : 'pointer',
          }}
        >
          {wasPoked ? <>✓ cutucado</> : isPoking ? '...' : <><span style={{ fontSize: 13 }}>👋</span> cutucar</>}
        </button>
      )}
    </div>
  );
};

// Reusable insight post card (from clubs-screen-inner.jsx)
export const InsightPostCard: React.FC<{ post: any }> = ({ post }) => (
  <div className="card" style={{ padding: 16 }}>
    {post.pinned && (
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="mono" style={{
          fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ember)',
        }}>
          ✦ destaque
        </span>
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar letter={post.author?.avatar || '?'} size={36}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{post.author?.name || 'Membro'}</span>
          {post.author?.verified && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="var(--ember)">
              <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
              <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        <div className="mono" style={{
          fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em', marginTop: 2,
        }}>
          {post.author?.role && `${post.author.role === 'Admin' ? 'Líder' : post.author.role} · `}{post.timeAgo}
        </div>
      </div>
      {post.chapter && (
        <span style={{
          padding: '3px 8px', borderRadius: 999,
          background: 'var(--paper-3)', fontSize: 10,
          fontFamily: 'Geist Mono, monospace',
          letterSpacing: '0.06em', color: 'var(--ink-2)',
        }}>{post.chapter}</span>
      )}
    </div>
    <div className="serif" style={{
      fontSize: 16, lineHeight: 1.4,
      marginTop: 12, color: 'var(--ink)', textWrap: 'pretty',
    }}>
      {(() => {
        const { segs, clamped } = splitMentionsClamped(post.text || '', 220);
        const nodes = segs.map((s, i) => s.kind === 'mention'
          ? <MentionChip key={i} type={s.type} id={s.id} handle={s.handle} label={s.label} />
          : <React.Fragment key={i}>{s.value}</React.Fragment>);
        return clamped ? (
          <>
            {nodes}…
            <button style={{
              color: 'var(--ember)', fontStyle: 'italic',
              fontFamily: 'inherit', fontSize: 'inherit', marginLeft: 4,
            }}>ver mais</button>
          </>
        ) : <MentionText text={post.text} />;
      })()}
    </div>
    {post.reactions && (
      <div style={{ marginTop: 14 }}>
        <ReactionRow r={post.reactions}/>
      </div>
    )}
    <button style={{
      marginTop: 12, width: '100%', textAlign: 'left', padding: '8px 0',
      borderTop: '1px solid var(--line)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'transparent',
    }}>
      <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        {post.comments} comentários
      </span>
      <Icon name="chevron-down" size={14} color="var(--ink-4)"/>
    </button>
  </div>
);
